import React from 'react';
import { Page } from '@/payload/payload-types';
import { Hero } from '../_blocks/Hero';
import { FilteredProducts } from '../_blocks/FilteredProducts';
import { Spacer } from '../_blocks/Spacer';
import { MenuTiles } from '../_blocks/MenuTiles';
import { CtaBanner } from '../_blocks/CtaBanner';
import { Reviews } from '../_blocks/Reviews';
import { ProgrammeCardBlock } from '../_blocks/ProgrammeCardBlock';
import { TabbedContent } from '../_blocks/TabbedContent';
import { InfoBoxes } from '../_blocks/InfoBoxes';
import { CtaButton } from '../_blocks/CtaButton';
import RichTextLexical from '../_components/RichTextLexical';

type Props = {
	layout?: Page['layoutBlocks'];
};

const RenderBlocks: React.FC<Props> = ({ layout }) => {
	if (!layout) return null;

	return (
		<>
			{layout.map((block, i) => {
				if (isHeroBlock(block)) {
					return <Hero key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isInfoboxesBlock(block)) {
					return <InfoBoxes key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isCtaButtonBlock(block)) {
					return <CtaButton key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isTabbedContent(block)) {
					return <TabbedContent key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isProgrammeCardBlock(block)) {
					return <ProgrammeCardBlock key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isReviews(block)) {
					return <Reviews key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isCtaBanner(block)) {
					return <CtaBanner key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isMenuTiles(block)) {
					return <MenuTiles key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isSpacer(block)) {
					return <Spacer key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isFilteredProducts(block)) {
					return <FilteredProducts key={`${block.blockType}-${i}`} {...block} />;
				}
				if (isRichTextEditorLexicalBlock(block)) {
					return (
						<RichTextLexical key={`${block.blockType}-${i}`} content={block.content} />
					);
				}
				return <div key={i}>Error: no block defined</div>;
			})}
		</>
	);
};

function isHeroBlock(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'hero' }> {
	return block.blockType === 'hero';
}

function isInfoboxesBlock(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'infoboxes' }> {
	return block.blockType === 'infoboxes';
}

function isCtaButtonBlock(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'ctaButton' }> {
	return block.blockType === 'ctaButton';
}

function isTabbedContent(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'tabbedContent' }> {
	return block.blockType === 'tabbedContent';
}

function isProgrammeCardBlock(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'programmeCardBlock' }> {
	return block.blockType === 'programmeCardBlock';
}

function isReviews(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'reviews' }> {
	return block.blockType === 'reviews';
}

function isCtaBanner(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'ctaBanner' }> {
	return block.blockType === 'ctaBanner';
}

function isMenuTiles(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'menuTiles' }> {
	return block.blockType === 'menuTiles';
}

function isSpacer(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'spacer' }> {
	return block.blockType === 'spacer';
}

function isFilteredProducts(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'filteredProducts' }> {
	return block.blockType === 'filteredProducts';
}

function isRichTextEditorLexicalBlock(
	block: Page['layoutBlocks'][number],
): block is Extract<Page['layoutBlocks'][number], { blockType: 'richTextEditorLexical' }> {
	return block.blockType === 'richTextEditorLexical';
}

export default RenderBlocks;
